import { useEffect, useMemo } from "react"

import { useSearchParams } from "react-router-dom"

import { AmplitudeEvent } from "@common-shared/config/amplitude.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { GENERAL_CONFIG } from "../config/general.config.ts"
import { AmplitudeHelper } from "../helpers/amplitude.helper.tsx"
import { CoverageHistoryHelper } from "../helpers/coverageHistory.helper.ts"
import { PoliciesHelper } from "../helpers/policies.helper.ts"
import { RuntimeHelper } from "../helpers/runtime.helper.ts"
import { useAppDispatch } from "../store/appDispatch.hook.ts"
import { useAppSelector } from "../store/selectors/app.selector.ts"
import { policiesSelector } from "../store/selectors/policies.selector.ts"
import { runtimeSelector } from "../store/selectors/runtime.selector.ts"
import { selectedPracticeRoleSelector } from "../store/selectors/selectedPracticeRole.selector.ts"
import { coverageOverridesSetSelectedPolicyOverride } from "../store/slices/coverageOverrides.slice.ts"
import { policiesSetSelectedItemComments } from "../store/slices/policies.slice.ts"
import { getSingleCoverageOverride } from "../store/thunks/coverageOverrides.thunks.ts"
import {
    policiesGetSinglePolicy,
    policiesReloadList,
    policiesResetSelectedItemAndRelatedData,
} from "../store/thunks/policies.thunks.ts"
import { policiesGetSinglePolicyComments } from "../store/thunks/policyComments.thunks.ts"
import { usePreviousValue } from "./previousValue.hook.ts"

export const useSinglePolicyView = (
    listType: PoliciesListType,
    nirvanaRequestId: string,
) => {
    const dispatch = useAppDispatch()
    const [searchParams] = useSearchParams()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const runtimeState = useAppSelector(runtimeSelector)
    const policiesState = useAppSelector(policiesSelector)
    const { selectedItem, selectedItemHistory } = policiesState

    const previousSelectedItem =
        usePreviousValue<IOneVerifyCoverageCheckHistory>(
            selectedItem || undefined,
        )

    //////// MEMOs
    const isRetryingCoverageCheckLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isRetrySinglePolicyLoading()
    }, [runtimeState.isLoading])

    const commonCoverageCheckResult = useMemo(() => {
        return {
            [CoverageHistoryHelper.getCoverageCheckNetwork(selectedItem)]:
                selectedItem,
        }
    }, [selectedItem])

    const isLatestCheck = useMemo<boolean>(() => {
        return PoliciesHelper.isLatestCheck(
            selectedItem?.coverageResult.resultId,
            selectedItemHistory,
        )
    }, [selectedItem?.coverageResult.resultId, selectedItemHistory])

    const {
        currentPolicyIdentificationData,
        currentPatientIdentificationData,
        previousPolicyIdentificationData,
        previousPatientIdentificationData,
    } = useMemo(() => {
        const currentPolicyIdentificationData =
            CoverageHistoryHelper.getPolicyIdentificationData(selectedItem)

        const currentPatientIdentificationData =
            CoverageHistoryHelper.getPatientIdentificationData(selectedItem)

        const previousPolicyIdentificationData =
            CoverageHistoryHelper.getPolicyIdentificationData(
                previousSelectedItem || null,
            )

        const previousPatientIdentificationData =
            CoverageHistoryHelper.getPatientIdentificationData(
                previousSelectedItem || null,
            )

        return {
            currentPolicyIdentificationData,
            currentPatientIdentificationData,
            previousPolicyIdentificationData,
            previousPatientIdentificationData,
        }
    }, [selectedItem, previousSelectedItem])

    //////// METHODS

    // reload list with same pagination
    // so new overridden value is in the list
    const reloadPoliciesList = () => {
        if (!selectedPracticeRole?.practice) {
            return
        }

        dispatch(
            policiesReloadList({
                practice: selectedPracticeRole.practice,
                payload: {
                    policiesViewType: listType,
                },
            }),
        )
    }

    const getSinglePolicy = (
        withPolicyDataReFetch?: boolean,
        onSuccess?: () => void,
    ) => {
        if (!selectedPracticeRole) {
            return
        }

        dispatch(
            policiesGetSinglePolicy({
                practice: selectedPracticeRole.practice,
                payload: {
                    retryMaxCount: 15,
                    nirvanaRequestId: nirvanaRequestId,
                    withPolicyDataReFetch,
                },
                onSuccess: () => {
                    onSuccess?.()

                    if (
                        searchParams.get(
                            GENERAL_CONFIG.urlSearchParamsKeys
                                .reloadPoliciesList,
                        )
                    ) {
                        reloadPoliciesList()
                    }
                },
            }),
        )
    }

    // EFFECTS

    // Log Amplitude event when policy is viewed
    useEffect(() => {
        if (selectedItem) {
            AmplitudeHelper.logEvent(AmplitudeEvent.VIEWED_POLICY, {
                practiceId: selectedPracticeRole?.practice.id,
                practiceName: selectedPracticeRole?.practice.name,
            })
        }
    }, [selectedItem?.coverageResult.resultId])

    // Get single coverage check from server
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        getSinglePolicy()

        // always clean related data when leaving page to prevent showing wrong data during next visit
        return () => {
            dispatch(policiesResetSelectedItemAndRelatedData())
        }
    }, [nirvanaRequestId, selectedPracticeRole?.practice.id])

    // (Re)load override data when selected item policy changes
    // (Re)load comments when selected item policy/patient changes
    // Compare current policy identification data with previous identification data, if they are different, load override for new data
    useEffect(() => {
        if (!selectedPracticeRole) {
            return
        }

        // HANDLE OVERRIDE
        if (currentPolicyIdentificationData) {
            if (
                JSON.stringify(currentPolicyIdentificationData) !==
                JSON.stringify(previousPolicyIdentificationData)
            ) {
                // Reload override data if policy identification data changed
                dispatch(
                    getSingleCoverageOverride({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            data: currentPolicyIdentificationData,
                        },
                    }),
                )
            }
        } else {
            // If there's no current policy identification data - reset override
            dispatch(coverageOverridesSetSelectedPolicyOverride(null))
        }

        // HANDLE COMMENTS
        if (
            currentPolicyIdentificationData ||
            currentPatientIdentificationData
        ) {
            if (
                JSON.stringify(currentPolicyIdentificationData) !==
                    JSON.stringify(previousPolicyIdentificationData) ||
                JSON.stringify(currentPatientIdentificationData) !==
                    JSON.stringify(previousPatientIdentificationData)
            ) {
                // Reload comments data if policy/patient identification data changed
                dispatch(
                    policiesGetSinglePolicyComments({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            patient:
                                currentPatientIdentificationData || undefined,
                            policy:
                                currentPolicyIdentificationData || undefined,
                        },
                    }),
                )
            }
        } else {
            // If there's no current policy/patient identification data - reset comments
            dispatch(policiesSetSelectedItemComments([]))
        }
    }, [
        JSON.stringify({
            currentPolicyIdentificationData,
            currentPatientIdentificationData,
        }),
    ])

    return {
        // DATA
        isRetryingCoverageCheckLoading,
        commonCoverageCheckResult,
        isLatestCheck,
        currentPolicyIdentificationData,
        currentPatientIdentificationData,
        // METHODS
        reloadPoliciesList,
        getSinglePolicy,
    }
}
